import { readFileSync, writeFileSync, mkdirSync, existsSync } from "node:fs";
import { join } from "node:path";
import { homedir } from "node:os";
import { VERSION } from "@fathom/core";

interface UpdateCheckCache {
  latest: string;
  checkedAt: string;
}

interface UpdateCheckResult {
  current: string;
  latest: string;
  updateAvailable: boolean;
}

const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

function getCachePath(): string {
  return join(homedir(), ".fathom", "update-check.json");
}

function readCache(): UpdateCheckCache | null {
  try {
    const cachePath = getCachePath();
    if (!existsSync(cachePath)) return null;
    const raw = readFileSync(cachePath, "utf-8");
    return JSON.parse(raw) as UpdateCheckCache;
  } catch {
    return null;
  }
}

function writeCache(latest: string): void {
  try {
    const cachePath = getCachePath();
    const dir = join(homedir(), ".fathom");
    if (!existsSync(dir)) {
      mkdirSync(dir, { recursive: true });
    }
    writeFileSync(
      cachePath,
      JSON.stringify({ latest, checkedAt: new Date().toISOString() }),
    );
  } catch {
    // Silently ignore write errors
  }
}

function isCacheFresh(cache: UpdateCheckCache): boolean {
  const checkedAt = new Date(cache.checkedAt).getTime();
  return Date.now() - checkedAt < CACHE_TTL_MS;
}

export async function checkForUpdate(): Promise<UpdateCheckResult | null> {
  try {
    const current = VERSION;

    // Check cache first
    const cache = readCache();
    if (cache && isCacheFresh(cache)) {
      return {
        current,
        latest: cache.latest,
        updateAvailable: cache.latest !== current,
      };
    }

    // Fetch from npm registry with 3s timeout
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);

    try {
      const response = await fetch(
        "https://registry.npmjs.org/fathom-cli/latest",
        { signal: controller.signal },
      );

      if (!response.ok) return null;

      const data = (await response.json()) as { version: string };
      const latest = data.version;

      writeCache(latest);

      return {
        current,
        latest,
        updateAvailable: latest !== current,
      };
    } finally {
      clearTimeout(timeout);
    }
  } catch {
    // Never block CLI — any error returns null
    return null;
  }
}
